/**
 * ARC-61 — wallet write guard (pure; no Firestore, no React).
 *
 * THE DEFECT, WHICH IS NOT QUITE THE OTHERS
 * `wallets` is a top-level field of `financial_data/{uid}`, so every save
 * REPLACES it wholesale from this client's snapshot — the same mechanic as
 * ARC-14 (expenses), ARC-58 (income), ARC-59 (risk) and ARC-60 (debts). But
 * unlike those, `wallets` never had a demo scaffold. Its exposure is the ARC-59
 * shape instead: SILENCE.
 *
 * Two of the three load branches never mentioned `wallets` at all:
 *
 *   1. the failure branch        — no assignment; state stays []   loadedOk FALSE
 *   2. the empty-payload branch  — no assignment; state stays []   loadedOk TRUE
 *   3. the loaded-document branch — setWallets(d.wallets ?? [])    loadedOk TRUE
 *
 * (2) arms autosave with an empty wallet list that nothing on screen or on the
 * server ever justified, and ~2s later that empty array replaces the member's
 * connected wallets. `??` on (3) is no protection either: it catches null and
 * undefined, so a stored value of any other unreadable shape passes straight
 * through to a tab that maps over it.
 *
 * That silence is why this module's central distinction is between an empty
 * list we CONFIRMED and an empty list we ASSUMED:
 *
 *   1. A read we do not trust produces no wallet state and no write permission
 *      — `resolveWalletLoad`. A read that FAILED and a read whose shape we
 *      cannot PARSE collapse to one identical closed state.
 *   2. `guardWalletWrite` refuses to write an empty list over a non-empty
 *      server one when no load ever confirmed what the server held. Once a load
 *      HAS confirmed it, an empty list is a member removing their last wallet,
 *      which is a real thing and stays allowed.
 *
 * Pure, so every failure path is testable without a Firestore.
 *
 * CUSTODY NOTE (unchanged by this issue): wallets here are public addresses
 * tracked read-only. Nothing in this module — or anywhere in this tab — holds
 * keys, signs, or moves member funds.
 *
 * Non-goals (ARC-61): nothing here touches wallet refresh, the Zerion service,
 * the address-masking preference or the wallet→category assignment map, all of
 * which live outside this document field.
 */

import { stableStringify } from "../expenses/expenseWriteGuard";

export { stableStringify };

export const WALLET_LOAD_ERROR =
  "Could not load your wallet data. Editing is disabled so nothing overwrites what is saved — please refresh the page.";

export const WALLET_OVERWRITE_BLOCKED =
  "Your saved wallets were protected: an empty wallet list was about to replace them before we could confirm what is stored. Nothing was overwritten.";

const isPlainObject = (v) =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/* Absent means "the member has connected no wallets yet" — a normal, safe
   state. Anything else that is not an array is a value we do not understand,
   which is NOT the same thing. */
const isAbsent = (v) => v === undefined || v === null;

const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

/**
 * Is a stored `wallets` value one we cannot read?
 *
 * A non-array, an array holding a non-record, or a record whose fields the tab
 * consumes unguarded are present but of the wrong type: `id` keys the list and
 * the category map, `chain` is read as a string (`w.chain.toUpperCase()`), and
 * `manualValue` / `lastValue` are summed into the member's total. A wrong-typed
 * value there is both a crash and — once coerced away — a wrong figure written
 * back as if the member had entered it.
 *
 * Absent optional fields are NOT unparseable: a wallet that has never been
 * refreshed legitimately has no `lastValue`.
 */
export const isUnparseableWallets = (v) => {
  if (isAbsent(v)) return false;
  if (!Array.isArray(v)) return true;
  return v.some((w) => {
    if (!isPlainObject(w)) return true;
    if (isAbsent(w.id) || (typeof w.id !== "string" && typeof w.id !== "number"))
      return true;
    if (!isAbsent(w.chain) && typeof w.chain !== "string") return true;
    if (!isAbsent(w.address) && typeof w.address !== "string") return true;
    if (!isAbsent(w.manualValue) && !isFiniteNumber(w.manualValue)) return true;
    if (!isAbsent(w.lastValue) && !isFiniteNumber(w.lastValue)) return true;
    return false;
  });
};

/**
 * Has the member changed the wallet buffer since the last server-confirmed
 * state? No committed snapshot means nothing has been confirmed yet — treat
 * that as "not dirty" so we never warn about changes the member did not make.
 */
export const hasUnsavedWalletChanges = (current, committed) => {
  if (!committed) return false;
  const shape = (s) => (Array.isArray(s?.wallets) ? s.wallets : []);
  return stableStringify(shape(current)) !== stableStringify(shape(committed));
};

/**
 * Decide the wallet state a load result is allowed to produce.
 *
 * @param {{ok: boolean, data?: object|null}} result
 *   `ok` is false when the read threw or the service reported failure.
 *   `data` is the loaded document, or null/empty for a brand-new account.
 *
 * @returns {{wallets: object[], committed: {wallets: object[]}|null,
 *   canSave: boolean, error: string}}
 *   `committed` being non-null is the whole point of this function: it is the
 *   record that a read actually reached the server and told us what is stored.
 *   `guardWalletWrite` will not let an empty list be written without it.
 */
export const resolveWalletLoad = ({ ok, data } = {}) => {
  /* One closed state, whatever the reason we do not trust the read. */
  const failedRead = () => ({
    wallets: [],
    committed: null,
    canSave: false,
    error: WALLET_LOAD_ERROR,
  });

  if (!ok) return failedRead();

  /* `??` used to let this value through to a tab that maps over it. Ignoring a
     value we cannot read is fine; replacing it with [] is the data loss this
     module exists to prevent. */
  if (isUnparseableWallets(data?.wallets)) return failedRead();

  /* A document with no `wallets` is either a brand-new account or one saved
     before the field existed. Both mean "the member has connected no wallets",
     and the read reached the server, so that absence is CONFIRMED — which is
     what makes it safe to write over later. */
  const wallets = Array.isArray(data?.wallets) ? data.wallets : [];

  return { wallets, committed: { wallets }, canSave: true, error: "" };
};

/**
 * Last gate before `wallets` goes into a write.
 *
 * @param {{outgoing: object[]|undefined, committed: {wallets: object[]}|null,
 *   server: object[]|undefined, outgoingTrusted: boolean}} params
 *   `server` is the document's current `wallets`, read immediately before the
 *   write. `committed` is our own last server-confirmed snapshot; null means no
 *   load ever told us what is stored. `outgoingTrusted` is false when the load
 *   that should have produced `outgoing` failed or could not be parsed.
 *
 * @returns {{wallets: object[], blocked: boolean, warning: string}}
 *
 * The value is substituted rather than the whole save being refused, for the
 * same reason as ARC-59 and ARC-60: one save carries every tab's state, so
 * refusing outright would strand a member editing a tab they never opened.
 *
 * The one rule that is specific to wallets: an empty outgoing list is blocked
 * when the member never emptied it. There is no way to tell "I removed my last
 * wallet" from "no load ever gave me one" by looking at the array — both are
 * []. What tells them apart is where the emptiness CAME FROM:
 *
 *   committed was NON-EMPTY, outgoing is empty  -> the member deleted wallets
 *                                                  they could see. Allowed.
 *   committed was empty or absent, outgoing is  -> the buffer never moved. The
 *   empty, and the server holds wallets            emptiness is the load's, not
 *                                                  the member's. Blocked.
 *
 * That second case covers more than the failed read. It also catches the
 * empty-payload branch — a read that reached the server and delivered nothing
 * while the document in fact holds wallets — and the concurrent-session case
 * where another device added a wallet after our load. In all three the member
 * did nothing, and a write of [] would be this client asserting an absence it
 * has no evidence for.
 */
export const guardWalletWrite = ({
  outgoing,
  committed,
  server,
  outgoingTrusted = true,
} = {}) => {
  const asList = (v) => (Array.isArray(v) ? v : []);

  /* We could not read this field, so `outgoing` is not the member's wallets —
     it is whatever the UI holds in their absence. Write the server's own value
     straight back, which makes the field a no-op for this save. Verbatim,
     including a value we failed to parse: we do not know it is safe to lose. */
  if (!outgoingTrusted) {
    return { wallets: asList(server), blocked: false, warning: "" };
  }

  const wallets = asList(outgoing);
  const allow = { wallets, blocked: false, warning: "" };

  if (wallets.length > 0) return allow;

  /* Nothing on the server to protect. */
  if (!Array.isArray(server) || server.length === 0) return allow;

  /* The member emptied a list they could see. Their call. */
  if (asList(committed?.wallets).length > 0) return allow;

  /* Empty in, empty snapshot, wallets on the server: the buffer never moved,
     so this write would delete them on the strength of an assumption. */
  return { wallets: server, blocked: true, warning: WALLET_OVERWRITE_BLOCKED };
};

export default resolveWalletLoad;
